/**
 * Registration form handling for Restrict Content Pro, without jQuery.
 *
 * Replaces RCP's register.js. It runs the same chain:
 *   1. Process the form via ajax (validates, creates the user, membership and payment).
 *   2. Fetch a fresh nonce (logging in the new user invalidated the old one).
 *   3. Store the payment ID in the form and submit it natively.
 */
/**
 * Initialise the registration form.
 *
 * @param {HTMLFormElement|null} form   The registration form.
 * @param {Object}               config Script configuration (ajaxUrl, pleaseWait, genericError).
 * @return {void}
 */
export function initRegistrationForm( form, config ) {
	if ( ! form || ! config || ! config.ajaxUrl || form.dataset.rcpRegisterInit ) {
		return;
	}

	// Guard against initialising the same form twice.
	form.dataset.rcpRegisterInit = 'true';

	let processing = false;

	const submitButton = form.querySelector( '#rcp_submit' );
	const buttonLabel = submitButton ? submitButton.textContent : '';

	const post = async ( body ) => {
		const response = await fetch( config.ajaxUrl, {
			method: 'POST',
			credentials: 'same-origin',
			body,
		} );
		return response.json();
	};

	const setField = ( name, value ) => {
		let field = form.elements[ name ];
		if ( ! field ) {
			field = document.createElement( 'input' );
			field.type = 'hidden';
			field.name = name;
			form.appendChild( field );
		}
		field.value = value;
	};

	const showErrors = ( html ) => {
		let container = form.querySelector( '#rcp-register-errors' );
		if ( ! container ) {
			container = document.createElement( 'div' );
			container.id = 'rcp-register-errors';
			container.setAttribute( 'role', 'alert' );
			const row = submitButton ? submitButton.closest( 'p' ) : null;
			form.insertBefore( container, row || null );
		}
		container.innerHTML = html || '';
	};

	const reset = () => {
		processing = false;
		if ( submitButton ) {
			submitButton.disabled = false;
			submitButton.textContent = buttonLabel;
		}
	};

	const refreshNonce = async () => {
		const result = await post( new URLSearchParams( { action: 'rcp_generate_registration_nonce' } ) );
		if ( result.success ) {
			setField( 'rcp_register_nonce', result.data );
		}
		return result.success;
	};

	form.addEventListener( 'submit', async ( event ) => {
		event.preventDefault();

		if ( processing ) {
			return;
		}

		processing = true;
		showErrors( '' );

		if ( submitButton ) {
			submitButton.disabled = true;
			submitButton.textContent = config.pleaseWait || submitButton.textContent;
		}

		try {
			const body = new FormData( form );
			body.set( 'action', 'rcp_process_register_form' );
			body.set( 'rcp_ajax', 'true' );

			const result = await post( body );

			if ( ! result.success ) {
				if ( result.data && result.data.nonce ) {
					setField( 'rcp_register_nonce', result.data.nonce );
				}
				showErrors( result.data && result.data.errors );
				reset();
				return;
			}

			if ( ! ( await refreshNonce() ) || ! result.data.payment_id ) {
				throw new Error( 'Registration could not be completed.' );
			}

			setField( 'rcp_registration_payment_id', result.data.payment_id );

			// Submitting natively skips the submit event, so there is no loop.
			form.submit();
		} catch ( error ) {
			showErrors( `<p class="c-site-form__error">${config.genericError || 'Something went wrong. Please try again.'}</p>` );
			reset();
		}
	} );
}

/**
 * Initialise every registration form on the page.
 *
 * The form can be rendered twice (on the registration page and in the login popup), and the popup
 * form is moved around the DOM rather than recreated, so it only needs to be initialised once.
 *
 * @return {void}
 */
function initAll() {
	document.querySelectorAll( 'form[id="registration-form"]' ).forEach( ( form ) => {
		initRegistrationForm( form, window.mediapoolRcpRegister );
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initAll );
} else {
	initAll();
}
